// Процедурная графика на клиенте (Canvas): тайлы карты, спрайты юнитов, текстуры.
// generateBuildingIcon / generateItemIcon — SVG из shared; здесь — растровые генераторы.
import { type RaceKey, type Terrain, XorShift128, hashCoord } from '@sk/shared';
import {
  PALETTE,
  TERRAIN_COLORS,
  generateBuildingIcon,
  generateItemIcon,
  toDataUri,
  unitSilhouette,
} from '@sk/shared/icons';

export { generateBuildingIcon, generateItemIcon };

function canvas(w: number, h: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return c;
}

function hexToRgb(hex: string): [number, number, number] {
  const n = Number.parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

const tileCache = new Map<string, HTMLCanvasElement>();

/** generateMapTile(terrain) → Canvas: процедурный шум + детали (деревья, скалы, волны, песок). */
export function generateMapTile(terrain: Terrain, variant = 0, size = 64): HTMLCanvasElement {
  const key = `${terrain}:${variant}:${size}`;
  const cached = tileCache.get(key);
  if (cached) return cached;
  const c = canvas(size, size);
  const ctx = c.getContext('2d')!;
  const col = TERRAIN_COLORS[terrain] ?? TERRAIN_COLORS.plain!;
  const [r, g, b] = hexToRgb(col.base);
  const img = ctx.createImageData(size, size);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const n =
        hashCoord(x >> 1, y >> 1, 97 + variant * 13) * 0.5 + hashCoord(x >> 3, y >> 3, 41 + variant) * 0.5;
      const k = 0.82 + n * 0.32;
      const i = (y * size + x) * 4;
      img.data[i] = Math.min(255, r * k);
      img.data[i + 1] = Math.min(255, g * k);
      img.data[i + 2] = Math.min(255, b * k);
      img.data[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  const rng = new XorShift128(`${terrain}:${variant}`);
  const s = size / 64;
  ctx.lineWidth = 2 * s;
  const tree = (x: number, y: number, k: number, pine: boolean) => {
    ctx.fillStyle = 'rgba(0,0,0,.22)';
    ctx.beginPath();
    ctx.ellipse(x + 3 * s * k, y, 7 * s * k, 3 * s * k, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#5a3a1e';
    ctx.fillRect(x - 1.2 * s * k, y - 6 * s * k, 2.4 * s * k, 6 * s * k);
    if (pine) {
      for (let i = 0; i < 3; i++) {
        const yy = y - 4 * s * k - i * 5 * s * k;
        const w = (8 - i * 2) * s * k;
        ctx.fillStyle = i % 2 ? col.dark : col.base;
        ctx.beginPath();
        ctx.moveTo(x, yy - 8 * s * k);
        ctx.lineTo(x + w, yy);
        ctx.lineTo(x - w, yy);
        ctx.closePath();
        ctx.fill();
      }
      if (terrain === 'snow') {
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.moveTo(x, y - 22 * s * k);
        ctx.lineTo(x + 3 * s * k, y - 17 * s * k);
        ctx.lineTo(x - 3 * s * k, y - 17 * s * k);
        ctx.fill();
      }
    } else {
      ctx.fillStyle = col.dark;
      ctx.beginPath();
      ctx.arc(x, y - 11 * s * k, 7.5 * s * k, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = col.light;
      ctx.beginPath();
      ctx.arc(x - 2.5 * s * k, y - 13.5 * s * k, 3.5 * s * k, 0, Math.PI * 2);
      ctx.fill();
    }
  };
  const peak = (x: number, y: number, w: number, h: number, snowCap: boolean) => {
    ctx.fillStyle = 'rgba(0,0,0,.25)';
    ctx.beginPath();
    ctx.ellipse(x + w * 0.2, y, w, 4 * s, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = col.light;
    ctx.beginPath();
    ctx.moveTo(x - w, y);
    ctx.lineTo(x, y - h);
    ctx.lineTo(x + w, y);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = col.dark;
    ctx.beginPath();
    ctx.moveTo(x, y - h);
    ctx.lineTo(x + w, y);
    ctx.lineTo(x + w * 0.15, y);
    ctx.closePath();
    ctx.fill();
    if (snowCap) {
      ctx.fillStyle = '#f4f7fa';
      ctx.beginPath();
      ctx.moveTo(x, y - h);
      ctx.lineTo(x + w * 0.32, y - h * 0.68);
      ctx.lineTo(x + w * 0.05, y - h * 0.74);
      ctx.lineTo(x - w * 0.3, y - h * 0.66);
      ctx.closePath();
      ctx.fill();
    }
  };
  switch (terrain) {
    case 'forest': {
      const n = 7 + rng.int(0, 2);
      const pts: [number, number][] = [];
      for (let i = 0; i < n; i++) pts.push([rng.int(8, 56) * s, rng.int(18, 62) * s]);
      pts.sort((a, b) => a[1] - b[1]);
      for (const [x, y] of pts) tree(x, y, 0.8 + rng.next() * 0.4, rng.next() < 0.45);
      break;
    }
    case 'mountain':
      peak(rng.int(20, 30) * s, rng.int(40, 48) * s, 16 * s, rng.int(24, 32) * s, true);
      peak(rng.int(36, 48) * s, rng.int(54, 60) * s, 18 * s, rng.int(26, 36) * s, rng.next() < 0.7);
      if (rng.next() < 0.5) peak(rng.int(8, 16) * s, 62 * s, 10 * s, 16 * s, false);
      break;
    case 'sea':
    case 'river':
      ctx.strokeStyle = col.light;
      ctx.lineWidth = 1.6 * s;
      for (let i = 0; i < (terrain === 'sea' ? 3 : 2); i++) {
        const y = rng.int(10, 56) * s;
        const x = rng.int(4, 36) * s;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.quadraticCurveTo(x + 5 * s, y - 3 * s, x + 10 * s, y);
        ctx.quadraticCurveTo(x + 15 * s, y + 3 * s, x + 20 * s, y);
        ctx.stroke();
      }
      break;
    case 'coast':
      ctx.fillStyle = col.dark;
      for (let i = 0; i < 18; i++) ctx.fillRect(rng.int(0, 62) * s, rng.int(0, 62) * s, 1.5 * s, 1.5 * s);
      if (rng.next() < 0.4) {
        ctx.fillStyle = '#8d939a';
        ctx.beginPath();
        ctx.ellipse(rng.int(14, 50) * s, rng.int(14, 50) * s, 4 * s, 2.5 * s, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      if (rng.next() < 0.35) tree(rng.int(14, 50) * s, rng.int(30, 58) * s, 0.7, false);
      break;
    case 'desert':
      ctx.strokeStyle = col.dark;
      ctx.lineWidth = 1.6 * s;
      for (let i = 0; i < 4; i++) {
        const y = rng.int(12, 58) * s;
        const x = rng.int(0, 30) * s;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.quadraticCurveTo(x + 14 * s, y - 7 * s, x + 30 * s, y);
        ctx.stroke();
      }
      if (rng.next() < 0.35) {
        const x = rng.int(12, 52) * s;
        const y = rng.int(24, 58) * s;
        ctx.fillStyle = '#4f7a3a';
        ctx.fillRect(x - 1.5 * s, y - 12 * s, 3 * s, 12 * s);
        ctx.fillRect(x - 6 * s, y - 9 * s, 2.5 * s, 5 * s);
        ctx.fillRect(x - 6 * s, y - 6 * s, 6 * s, 2 * s);
      }
      break;
    case 'snow':
      ctx.fillStyle = col.dark;
      for (let i = 0; i < 3; i++) {
        ctx.beginPath();
        ctx.ellipse(rng.int(8, 56) * s, rng.int(10, 58) * s, 9 * s, 2.5 * s, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      if (rng.next() < 0.45) tree(rng.int(14, 50) * s, rng.int(30, 60) * s, 0.9, true);
      break;
    case 'plain': {
      ctx.strokeStyle = col.light;
      ctx.lineWidth = 1.5 * s;
      for (let i = 0; i < 9; i++) {
        const x = rng.int(4, 60) * s;
        const y = rng.int(6, 60) * s;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x + rng.int(-2, 2) * s, y - 5 * s);
        ctx.stroke();
      }
      const flowers = ['#f2e05a', '#ffffff', '#e86a6a', '#b48be0'];
      for (let i = 0; i < 4; i++) {
        ctx.fillStyle = flowers[rng.int(0, 3)]!;
        ctx.fillRect(rng.int(4, 60) * s, rng.int(4, 60) * s, 2 * s, 2 * s);
      }
      if (rng.next() < 0.18) tree(rng.int(14, 50) * s, rng.int(30, 60) * s, 0.75, false);
      if (rng.next() < 0.12) {
        ctx.fillStyle = '#8d939a';
        ctx.beginPath();
        ctx.ellipse(rng.int(10, 54) * s, rng.int(10, 54) * s, 3.5 * s, 2.2 * s, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    }
  }
  tileCache.set(key, c);
  return c;
}

const blobCache = new Map<string, HTMLCanvasElement>();
/** Доля «нахлёста» клетки на соседей (плавные границы местности, как в «Героях»). */
export const BLOB_PAD = 0.2;

/**
 * Клетка-«пятно»: текстура местности в скруглённом квадрате на 20% больше клетки с каждой стороны.
 * Клетки рисуются от низшего приоритета к высшему — суша наплывает на море, лес на равнину, горы на всё.
 */
export function generateMapBlob(terrain: Terrain, variant = 0, size = 64): HTMLCanvasElement {
  const key = `${terrain}:${variant}:${size}`;
  const cached = blobCache.get(key);
  if (cached) return cached;
  const pad = Math.round(size * BLOB_PAD);
  const S = size + pad * 2;
  const c = canvas(S, S);
  const ctx = c.getContext('2d')!;
  const tile = generateMapTile(terrain, variant, size);
  const pattern = ctx.createPattern(tile, 'repeat')!;
  pattern.setTransform(new DOMMatrix().translateSelf(pad, pad));
  ctx.fillStyle = pattern;
  ctx.beginPath();
  ctx.roundRect(0, 0, S, S, S * 0.42);
  ctx.fill();
  // Детали (деревья, вершины) — поверх, в границах клетки
  ctx.drawImage(tile, pad, pad);
  blobCache.set(key, c);
  return c;
}

/** Порядок наложения местности: выше — рисуется позже и наплывает на соседей. */
export const TERRAIN_PRIORITY: Record<Terrain, number> = {
  sea: 0,
  river: 1,
  coast: 2,
  desert: 3,
  snow: 3,
  plain: 4,
  forest: 5,
  hill: 5,
  bridge: 2,
  road: 3,
  mountain: 6,
};

const imgCache = new Map<string, HTMLImageElement>();
/** Подписчики на загрузку изображений (рендереры карты перерисовываются, когда иконка готова). */
export const imageLoadListeners = new Set<() => void>();

/** SVG → Image (кэш). Для отрисовки иконок объектов на Canvas. */
export function svgImage(svg: string): HTMLImageElement {
  let img = imgCache.get(svg);
  if (!img) {
    img = new Image();
    img.decoding = 'async';
    img.onload = () => {
      for (const fn of imageLoadListeners) fn();
    };
    img.src = toDataUri(svg);
    imgCache.set(svg, img);
  }
  return img;
}

/** generateUnitSprite(type, race) → Canvas: силуэт юнита с тенью (для анимаций карты/боя). */
export function generateUnitSprite(unitKey: string, race: RaceKey, size = 48): Promise<HTMLCanvasElement> {
  return new Promise((resolve) => {
    const c = canvas(size, size);
    const img = svgImage(unitSilhouette(unitKey, race));
    const draw = () => {
      const ctx = c.getContext('2d')!;
      ctx.fillStyle = 'rgba(0,0,0,.35)';
      ctx.beginPath();
      ctx.ellipse(size / 2, size * 0.9, size * 0.3, size * 0.07, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.drawImage(img, 0, 0, size, size);
      resolve(c);
    };
    if (img.complete && img.naturalWidth) draw();
    else img.onload = draw;
  });
}

/** Пергамент: процедурный шум для фонов панелей (data URL, кэшируется). */
let parchment: string | null = null;
export function parchmentTexture(): string {
  if (parchment) return parchment;
  const c = canvas(128, 128);
  const ctx = c.getContext('2d')!;
  const img = ctx.createImageData(128, 128);
  const [r, g, b] = hexToRgb(PALETTE.parchment);
  for (let i = 0; i < 128 * 128; i++) {
    const x = i % 128;
    const y = Math.floor(i / 128);
    const n = hashCoord(x, y, 7) * 0.15 + hashCoord(x >> 3, y >> 3, 3) * 0.15;
    img.data[i * 4] = r * (0.85 + n);
    img.data[i * 4 + 1] = g * (0.85 + n);
    img.data[i * 4 + 2] = b * (0.82 + n);
    img.data[i * 4 + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
  parchment = c.toDataURL();
  return parchment;
}

/** Небо: градиент день/ночь по местному времени. */
export function skyGradient(date = new Date()): string {
  const h = date.getHours() + date.getMinutes() / 60;
  const day = Math.max(0, Math.sin(((h - 6) / 12) * Math.PI));
  const top = `rgb(${Math.round(12 + 90 * day)},${Math.round(24 + 130 * day)},${Math.round(44 + 170 * day)})`;
  const bottom = `rgb(${Math.round(30 + 190 * day)},${Math.round(45 + 150 * day)},${Math.round(70 + 120 * day)})`;
  return `linear-gradient(180deg, ${top} 0%, ${bottom} 100%)`;
}
